import Button from 'components/Button';
import Error from 'components/form/Error';
import ActionModal from 'components/Modal/ActionModal';
import Space from 'components/Space';
import { FC, useContext, useState } from 'react';
import { mutate } from 'swr';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate, getLocaleTime } from 'utils/time';
import { ReviewPageContext } from '.';
import { ButtonsContainer, Container } from './components';

// --- UTILITAIRE D'IDENTIFICATION ---
const getIsAuthor = (interview: any) => {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  let currentId = localStorage.getItem('userId');
  if (token) {
    try {
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      currentId = payload.id || payload.sub || payload.userId;
    } catch (e) {}
  }
  return (interview as any)?.isAuthor || 
    (!!currentId && !!interview?.authorId && String(currentId) === String(interview.authorId));
};

// --- COMPOSANT PRINCIPAL ---
const Accepted: FC = () => {
  const { interview } = useContext(ReviewPageContext);

  const dateDifference = interview?.date
    ? new Date().getTime() - new Date(interview?.date).getTime()
    : undefined;

  return dateDifference && dateDifference < 0 ? (
    <AcceptedBeforeDate />
  ) : (
    <AcceptedAfterDate />
  );
};

// --- COMPOSANT : AVANT LA DATE (SANS CALENDRIER) ---
const AcceptedBeforeDate: FC = () => {
  const { interview } = useContext(ReviewPageContext);
  const isAuthor = getIsAuthor(interview);

  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleCancel = () => {
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/interview/mentee-cancel/${interview?.id}`)
      .then(() => mutate(`/interview/mentee-all`))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

return (
    <Container>
      <p>
        {isAuthor ? "Votre mentor a accepté l'entretien du " : "Vous avez accepté l'entretien du "}
        {interview?.date && getLocaleDate(interview.date)} à{' '}
        {interview?.date && getLocaleTime(interview.date)}
        <span style={{ color: 'var(--gray-500)', fontWeight: 300 }}>*</span>.
      </p>
      
      <Space height={2} />
      
      <p>
        Cet entretien est confirmé. Vous pouvez l'annuler à tout moment à l'aide du bouton ci-dessous en cas d'empêchement.
      </p>
      
      <Space height={3} />
      
      <ButtonsContainer>
        <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
          Annuler l'entretien
        </Button>
      </ButtonsContainer>

      <ActionModal
        open={cancelOpen}
        title="Annuler l'entretien"
        apply={handleCancel}
        applyText="Annuler l'entretien"
        color="error"
        cancel={() => setCancelOpen(false)}
        close={() => setCancelOpen(false)}
        loading={loading}
      >
        <p>
          Vous êtes sur le point d'annuler l'entretien prévu à la date du{' '}
          {interview?.date && getLocaleDate(interview.date)} à{' '}
          {interview?.date && getLocaleTime(interview.date)}.
        </p>
        <Space height={1} />
        <p>
          Cette action est irréversible. Souhaitez-vous vraiment annuler cet entretien ?
        </p>
      </ActionModal>

      {error && <Error>Une erreur est survenue lors de l'annulation.</Error>}

      <Space height={3} />
      
      <p>
        Vous pouvez également rejoindre votre salon de visio-conférence à l'aide du bouton ci-dessous.
      </p>
      
      <ButtonsContainer>
        <a
          href={`https://meet.jit.si/mentorat-sextant-n-${interview?.mentorship?.mentorId}-${interview?.mentorship?.menteeId}-${interview?.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button color="primary" variant="outlined">
            Rejoindre la visio-conférence
          </Button>
        </a>
      </ButtonsContainer>
    </Container>
  );
};

// --- COMPOSANT : APRÈS LA DATE ---
const AcceptedAfterDate: FC = () => {
  const { interview } = useContext(ReviewPageContext);
  const isAuthor = getIsAuthor(interview);

  const [lateOpen, setLateOpen] = useState<boolean>(false);
  const [lateLoading, setLateLoading] = useState<boolean>(false);
  const [lateError, setLateError] = useState<boolean>(false);

  const handleLate = () => {
    setLateLoading(true);
    setLateError(false);
    privateAPI
      .post(`/interview/mentee-absent/${interview?.id}`)
      .then(() => mutate(`/interview/mentee-all`))
      .catch(() => setLateError(true))
      .finally(() => setLateLoading(false));
  };

  return (
    <Container>
      <p>
        {isAuthor ? <>Votre mentor a accepté l'entretien du </> : <>Vous avez accepté l'entretien du </>}
        {interview!.date && getLocaleDate(interview!.date)} à{' '}
        {interview?.date && getLocaleTime(interview.date)}
        <span style={{ color: 'var(--gray-500)', fontWeight: 300 }}>*</span>.
      </p>
      <Space height={3} />
      <p>
        Une fois votre entretien terminé, il faut que votre mentor l'indique depuis le portail.
      </p>
      <Space height={2} />
      <p>
        Si {interview?.mentorship?.mentor?.firstName} ne s'est pas présenté(e), vous pouvez le signaler.
      </p>
      <ButtonsContainer>
        <Button color="error" variant="outlined" onClick={() => setLateOpen(true)}>
          {interview?.mentorship?.mentor?.firstName} ne s'est pas présenté(e)
        </Button>
      </ButtonsContainer>

      <ActionModal
        open={lateOpen}
        title="Signaler une absence"
        apply={handleLate}
        applyText="Signaler l'absence"
        color="error"
        cancel={() => setLateOpen(false)}
        close={() => setLateOpen(false)}
        loading={lateLoading}
      >
        Souhaitez-vous signaler que le mentor ne s'est pas présenté à l'entretien ?
      </ActionModal>
      {lateError && <Error>Une erreur est survenue.</Error>}
    </Container>
  );
};

export default Accepted;